// src/lib/api.ts
// Minimal fetch wrapper for CP calls (no mocks).
// Base: VITE_CP_BASE (default '/cp').
// JWT: from localStorage('edge_jwt') or VITE_EDGE_JWT (for local dev only).

import { readDcAuthFromStorage } from './auth';

export function getBase(): string {
  const fromEnv = (import.meta as any)?.env?.VITE_CP_BASE;
  return fromEnv && String(fromEnv).length ? String(fromEnv) : '/cp';
}

export function getJwt(): string | null {
  if (typeof window !== 'undefined') {
    const dcAuth = readDcAuthFromStorage();
    if (dcAuth.token) return dcAuth.token;

    const edgeJwt = window.localStorage.getItem('edge_jwt');
    if (edgeJwt) return edgeJwt;
  }
  const env = ((import.meta as any)?.env?.VITE_EDGE_JWT as string) || null;
  return env || null;
}

type Opts = RequestInit & { asJson?: boolean };

function getTenantId(): string {
  try {
    const dcAuth = readDcAuthFromStorage();
    if (dcAuth.tenant) return String(dcAuth.tenant);
    return 'dev';
  } catch {
    return 'dev';
  }
}

function getAppId(): string | null {
  try {
    const appId = localStorage.getItem('dc.app_id');
    return appId ? String(appId) : null;
  } catch {
    return null;
  }
}

function isFormDataLike(body: any): boolean {
  if (!body || typeof body !== 'object') return false;
  // Covers cross-realm FormData and polyfills.
  // eslint-disable-next-line no-undef
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (Object.prototype.toString.call(body) === '[object FormData]') return true;
  return typeof body.append === 'function' && typeof body.get === 'function';
}

export async function apiFetch<T = unknown>(path: string, opts: Opts = {}): Promise<T> {
  const base = getBase();
  const token = getJwt();
  // Normalize headers (opts.headers can be Headers, [k,v][], or object)
  const headers = new Headers((opts as any).headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Required by Kong Gateway + used for partitioning in CP services
  if (!headers.has('x-tenant-id')) headers.set('x-tenant-id', getTenantId());
  const resolvedAppId = getAppId();
  if (!headers.has('x-app-id') && resolvedAppId) headers.set('x-app-id', resolvedAppId);

  // If the body is FormData, let the browser set the multipart boundary.
  // Also allow callers to override Content-Type explicitly.
  const bodyAny = (opts as any).body;
  const isFormData = isFormDataLike(bodyAny);

  if (!isFormData && !headers.has('Content-Type') && opts.asJson !== false) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${base}${path}`, { ...opts, headers });
  const bodyText = await res.text().catch(() => '');
  if (!res.ok) {
    throw new Error(`API ${res.status} ${res.statusText}: ${bodyText}`);
  }
  return (opts.asJson === false ? (bodyText as any) : JSON.parse(bodyText)) as T;
}

// Export axios-like apiClient for compatibility
export const apiClient = {
  get: <T = unknown>(url: string) => apiFetch<T>(url),
  post: <T = unknown>(url: string, data?: any, opts: Omit<Opts, 'method' | 'body'> = {}) =>
    apiFetch<T>(url, {
      ...opts,
      method: 'POST',
      body: isFormDataLike(data) ? data : JSON.stringify(data),
    }),
  put: <T = unknown>(url: string, data?: any, opts: Omit<Opts, 'method' | 'body'> = {}) =>
    apiFetch<T>(url, {
      ...opts,
      method: 'PUT',
      body: isFormDataLike(data) ? data : JSON.stringify(data),
    }),
  patch: <T = unknown>(url: string, data?: any, opts: Omit<Opts, 'method' | 'body'> = {}) =>
    apiFetch<T>(url, {
      ...opts,
      method: 'PATCH',
      body: isFormDataLike(data) ? data : JSON.stringify(data),
    }),
  delete: <T = unknown>(url: string) => 
    apiFetch<T>(url, { method: 'DELETE' }),
};


